import { fetchAndActivate, getValue } from 'firebase/remote-config'
import { remoteConfig } from './app'

let activated: Promise<boolean> | null = null

/** Fetch + activate Remote Config once; safe to call repeatedly. */
export function ensureRemoteConfig(): Promise<boolean> {
  if (!activated) {
    activated = fetchAndActivate(remoteConfig).catch((error) => {
      console.error('Remote config fetch failed', error)
      return false
    })
  }
  return activated
}

function rcString(key: string): string {
  return getValue(remoteConfig, key).asString()
}

function rcNumber(key: string): number {
  return getValue(remoteConfig, key).asNumber()
}

/** Boolean flag, falling back when the key isn't set in the template. */
function rcBoolean(key: string, fallback: boolean): boolean {
  const value = getValue(remoteConfig, key)
  return value.getSource() === 'static' ? fallback : value.asBoolean()
}

/** JSON array of strings, falling back when the key is missing or malformed. */
function rcStringArray(key: string, fallback: string[]): string[] {
  try {
    const parsed: unknown = JSON.parse(rcString(key) || '[]')
    if (!Array.isArray(parsed) || parsed.length === 0) return fallback
    return parsed.map(String)
  } catch {
    return fallback
  }
}

/** API base URL. Dev always targets the dev server; prod lets Remote Config override the default. */
export function getServer(): string {
  if (import.meta.env.DEV) {
    return import.meta.env.VITE_API_BASE ?? 'https://dev.voyp.app'
  }
  return rcString('server') || import.meta.env.VITE_API_BASE || 'https://api.voyp.app'
}

export function getWsServer(): string {
  return getServer().replace('http', 'ws')
}

export interface CallSuggestion {
  id?: string
  title: string
  description: string
  text: string
}

export const rc = {
  defaultTtsProvider: () => rcString('default_tts_provider'),
  contactsAccess: () => rcString('contacts_access'),
  calendarAccess: () => rcString('calendar'),
  stripePricingTable: () => rcString('stripe_pricing_table'),
  stripePricingTableBusiness: () => rcString('stripe_pricing_table_business'),
  stripeCreditsTable: () => rcString('stripe_credits_table'),
  stripePublicKey: () => rcString('stripe_public_key'),
  /** Stripe customer portal login link, for Stripe subscriptions RevenueCat has no management URL for. */
  stripeCustomerPortal: () => rcString('stripe_customer_portal'),
  /** RevenueCat public key; the template's Web condition serves the web (`strp_…`) key. */
  revenueCatWebKey: () => rcString('revenuecat'),
  /** Language models the user can pick from. */
  llms: () => rcStringArray('llms', ['chatgpt', 'claude', 'gemini', 'grok']),
  /** Language models that support advanced (realtime speech-to-speech) mode. */
  llmsAdvanced: () => rcStringArray('llms_advanced', ['chatgpt', 'gemini', 'grok']),
  maxUploadSize: () => rcNumber('max_upload_size'),
  maxUploadFiles: () => rcNumber('max_upload_files'),
  /** Test call: chat with the assistant as the callee before dialing. */
  testCall: () => rcBoolean('test_call', true),
  testCallRequireSubscription: () => rcBoolean('test_call_require_subscription', false),
  maxTestMessages: () => rcNumber('max_test_messages') || 20,
  /** What the callee says first when the assistant starts the conversation. */
  initialPrompt: () => rcString('initial_prompt') || 'Hello, how can I help?',
  /** Primer pair that casts the user as the callee in a test call. */
  initialUserMessage: () =>
    rcString('initial_user_message') ||
    'I will act as the person on the phone, you reply as the AI assistant to achieve the goal of the call',
  initialAssistantMessage: () =>
    rcString('initial_assistant_message') || 'OK, understood, conversation starts now',
  /** `[name]` is replaced with the user's name. */
  copilotFunctionDescription: () =>
    rcString('copilot_function_description') ||
    "always ask [name] to confirm details if you don't have enough information",
  copilotQuestionDescription: () =>
    rcString('copilot_question_description') || 'Question to ask [name] for more details',
  callSuggestions: (): CallSuggestion[] => {
    try {
      return JSON.parse(rcString('call_suggestions') || '[]')
    } catch {
      return []
    }
  },
}
